// ============================================================================
// useColumnWidths — изменение ШИРИНЫ колонок таблиц списков с сохранением в
// ХРАНИЛИЩЕ НАСТРОЕК ПОЛЬЗОВАТЕЛЯ (settingsStore; ключ `list.{listKey}.colWidths`).
// ЕДИНАЯ точка для списков (вкладка «Задачи» у заявки, дин.список «Задачи по
// заявке» и др.): перетаскивание правой границы заголовка (pointer events —
// мышь и тач); ширина пиксельная, сохраняется per-пользователь (SSoT — сервер).
// ============================================================================

import { useCallback, useRef, useState } from 'react';
import { settingsStore } from '@/lib/settingsStore';

export interface ColumnWidthsApi {
  /** Итоговые ширины (сохранённые поверх дефолтных) */
  widths: Record<string, number>;
  /** Начать перетаскивание границы колонки (pointerdown на ручке в th) */
  startResize: (col: string, e: React.PointerEvent) => void;
}

export function useColumnWidths(listKey: string, defaults: Record<string, number>): ColumnWidthsApi {
  const storageKey = `list.${listKey}.colWidths`;
  const [widths, setWidths] = useState<Record<string, number>>(() => ({
    ...defaults,
    ...(settingsStore.getJSON<Record<string, number>>(storageKey, {}) || {}),
  }));
  // Актуальные ширины для записи по окончании перетаскивания (без замыканий)
  const widthsRef = useRef(widths);
  widthsRef.current = widths;

  const startResize = useCallback((col: string, e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    // Начальная ширина: ФАКТИЧЕСКАЯ ширина колонки в момент захвата (th, в
    // котором лежит ручка) — без «прыжка» при первом движении у колонок без
    // дефолта/сохранённой ширины (дин. списки с произвольным составом колонок);
    // далее — сохранённая/дефолтная, иначе 120.
    const measured = Math.round(e.currentTarget.parentElement?.getBoundingClientRect().width || 0);
    const startW = measured || widthsRef.current[col] || defaults[col] || 120;
    const onMove = (ev: PointerEvent) => {
      const w = Math.max(48, Math.round(startW + ev.clientX - startX));
      setWidths((prev) => ({ ...prev, [col]: w }));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      // Сохранение в настройки пользователя (settingsStore дебаунсит запись)
      settingsStore.setJSON(storageKey, widthsRef.current);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [defaults, storageKey]);

  return { widths, startResize };
}
